import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Box, Button, CircularProgress, Paper, Snackbar, Stack, Typography } from "@mui/material";
import { Add, Check, ContentCopy } from "@mui/icons-material";
import { useAuth } from "../../../contexts/AuthContext";
import { Product } from "../../../types";
import { answerDeskService, FaqEntry } from "../../../services/answerDeskService";
import { DeliveryArea, deliveryAreaService } from "../../../services/deliveryAreaService";
import { copyText, fillAnswer, segmentsToText } from "../answerDeskUtils";
import { AnswerText } from "./AnswerCard";
import { AnswerImages } from "./AnswerImages";
import { AnswerEditorDialog } from "./AnswerEditorDialog";

interface ProductAnswersSectionProps {
    product: Product;
    /** Whether the host drawer is open — entries are refreshed on each open. */
    open: boolean;
}

// Keeps keys typed in the editor (e.g. ← / → moving the caret) from
// reaching window-level shortcuts such as the drawer's prev/next product.
const stopKeys = (e: React.KeyboardEvent) => e.stopPropagation();

/** "Customer questions" in the product drawer: the published Answer Desk
 * answers whose rules match this product, ready to copy. */
export const ProductAnswersSection: React.FC<ProductAnswersSectionProps> = ({ product, open }) => {
    const { user } = useAuth();
    const canPublish = user?.role === "OWNER" || user?.role === "MANAGER";
    const productId = product.product_id;

    const [entries, setEntries] = useState<FaqEntry[] | null>(null);
    const [matchedIds, setMatchedIds] = useState<string[] | null>(null);
    const [deliveryAreas, setDeliveryAreas] = useState<DeliveryArea[]>([]);
    const [entriesFailed, setEntriesFailed] = useState(false);
    const [matchFailed, setMatchFailed] = useState(false);
    const [editorOpen, setEditorOpen] = useState(false);
    const [copiedId, setCopiedId] = useState<string | null>(null);
    const [toast, setToast] = useState<{ message: string; severity: "success" | "error" } | null>(null);
    const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    // The entry list doesn't depend on the product — load it once per
    // drawer open, not on every ← / → step.
    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        setEntriesFailed(false);
        answerDeskService
            .listEntries()
            .then((list) => {
                if (!cancelled) setEntries(list.filter((e) => e.status === "PUBLISHED"));
            })
            .catch(() => {
                if (!cancelled) setEntriesFailed(true);
            });
        return () => {
            cancelled = true;
        };
    }, [open]);

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        setMatchedIds(null);
        setMatchFailed(false);
        answerDeskService
            .matchProducts([productId])
            .then((map) => {
                if (!cancelled) setMatchedIds((map[productId] ?? []).map((m) => m.faq_entry_id));
            })
            .catch(() => {
                if (!cancelled) setMatchFailed(true);
            });
        return () => {
            cancelled = true;
        };
    }, [open, productId]);

    useEffect(() => {
        let cancelled = false;
        deliveryAreaService
            .getDeliveryAreas(0, 500, true)
            .then((res) => {
                if (!cancelled) setDeliveryAreas(res.items ?? []);
            })
            .catch(() => {
                /* {delivery_charges} just stays unfilled */
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => () => {
        if (copiedTimer.current) clearTimeout(copiedTimer.current);
    }, []);

    const copy = useCallback(
        async (entry: FaqEntry) => {
            const text = segmentsToText(fillAnswer(entry.answer ?? "", product, deliveryAreas));
            if (await copyText(text)) {
                setCopiedId(entry.faq_entry_id);
                if (copiedTimer.current) clearTimeout(copiedTimer.current);
                copiedTimer.current = setTimeout(() => setCopiedId(null), 2000);
                answerDeskService.recordCopy(entry.faq_entry_id);
            } else {
                setToast({ message: "Couldn't copy — select the text and copy it manually.", severity: "error" });
            }
        },
        [product, deliveryAreas],
    );

    // Match order is most specific first (product, then brand/category).
    const byId = new Map((entries ?? []).map((e) => [e.faq_entry_id, e]));
    const answers = (matchedIds ?? []).map((id) => byId.get(id)).filter((e): e is FaqEntry => !!e);
    const failed = (entriesFailed && entries === null) || matchFailed;
    const loading = !failed && (entries === null || matchedIds === null);

    return (
        <>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                CUSTOMER QUESTIONS{answers.length > 0 ? ` (${answers.length})` : ""}
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                        <CircularProgress size={20} />
                    </Box>
                ) : failed ? (
                    <Typography variant="body2" color="text.secondary">
                        Couldn't load the answers for this product.
                    </Typography>
                ) : answers.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        No answers for this product yet.
                    </Typography>
                ) : (
                    answers.map((entry, idx) => (
                        <Box
                            key={entry.faq_entry_id}
                            sx={{
                                py: 1.25,
                                pt: idx === 0 ? 0 : 1.25,
                                borderBottom: idx < answers.length - 1 ? "1px solid" : "none",
                                borderColor: "divider",
                            }}
                        >
                            <Stack direction="row" spacing={1} alignItems="flex-start">
                                <Typography variant="body2" fontWeight="medium" sx={{ flex: 1, minWidth: 0, pt: 0.5 }}>
                                    {entry.question}
                                </Typography>
                                <Button
                                    size="small"
                                    color={copiedId === entry.faq_entry_id ? "success" : "primary"}
                                    startIcon={copiedId === entry.faq_entry_id ? <Check /> : <ContentCopy fontSize="small" />}
                                    onClick={() => copy(entry)}
                                    aria-label={`Copy the answer to “${entry.question}”`}
                                    sx={{ flexShrink: 0 }}
                                >
                                    {copiedId === entry.faq_entry_id ? "Copied" : "Copy"}
                                </Button>
                            </Stack>
                            <Box sx={{ color: "text.secondary", mt: 0.25 }}>
                                <AnswerText segments={fillAnswer(entry.answer ?? "", product, deliveryAreas)} dense />
                            </Box>
                            {entry.images?.length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                    <AnswerImages images={entry.images} question={entry.question} size={64} />
                                </Box>
                            )}
                            {entry.internal_note && (
                                <Typography variant="caption" color="warning.dark" display="block" sx={{ mt: 0.5 }}>
                                    For you, not the customer: {entry.internal_note}
                                </Typography>
                            )}
                        </Box>
                    ))
                )}
                <Button
                    size="small"
                    startIcon={<Add fontSize="small" />}
                    onClick={() => setEditorOpen(true)}
                    sx={{ mt: 1.5, px: 0.5 }}
                >
                    {canPublish ? "Add answer for this product" : "Suggest an answer for this product"}
                </Button>
            </Paper>

            <span onKeyDown={stopKeys}>
                <AnswerEditorDialog
                    open={editorOpen}
                    entry={null}
                    prefill={{ products: [product] }}
                    previewProducts={[product]}
                    canPublish={canPublish}
                    deliveryAreas={deliveryAreas}
                    onClose={() => setEditorOpen(false)}
                    onSaved={(saved, message) => {
                        setEditorOpen(false);
                        if (saved?.status === "PUBLISHED") {
                            setEntries((prev) => [saved, ...(prev ?? []).filter((e) => e.faq_entry_id !== saved.faq_entry_id)]);
                            if (saved.targets.some((t) => t.product_id === productId)) {
                                setMatchedIds((prev) => [saved.faq_entry_id, ...(prev ?? []).filter((id) => id !== saved.faq_entry_id)]);
                            }
                        }
                        setToast({ message, severity: "success" });
                    }}
                />
            </span>
            <Snackbar
                open={!!toast}
                autoHideDuration={3000}
                onClose={() => setToast(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert onClose={() => setToast(null)} severity={toast?.severity ?? "success"} variant="filled" sx={{ width: "100%" }}>
                    {toast?.message}
                </Alert>
            </Snackbar>
        </>
    );
};
